import React, { useEffect, useState } from "react";
import { Lock, X, Check, CreditCard, Loader2, Tag, ShieldCheck, Mail, RotateCcw, Crown } from "lucide-react";
import { PLAN_ORDER, PLAN_META, PAYWALL_REASONS, planHighlights, loadPlanQuotas } from "@/lib/plans";
import { PlanCompare } from "@/components/shared/PlanCompare";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { StripePaymentForm } from "./StripePaymentForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Plan copy lives in lib/plans.js (plan audit 2026-09-28) -- the old
// TIER_FEATURES list here promised features nothing enforced.

// Shared checkout UI (subscription tiers, Stripe + PayPal) --
// extracted from ResumeEditor so the same, real, working flow can be
// reused anywhere an "Upgrade" CTA needs to actually open checkout,
// instead of each caller either duplicating this or (as on the real
// /settings page) silently doing nothing because no onUpgrade was wired.
// `isLight` defaults to false (the previous hardcoded-dark look) so a caller
// that doesn't pass it renders exactly as before rather than breaking. Every
// in-app call site does pass it -- this modal was the last high-traffic one
// still ignoring the theme, so in light mode it opened as a dark slab over a
// white page. Tokens below mirror GrammarCheckModal/AccountSettings, which
// were already theme-aware.
// `locked` (2026-10-01, "block everything after the free limit"): the
// window can't be dismissed -- no close button, backdrop clicks ignored,
// no "Continue with Free" -- and the footer offers only Sign out / Log in
// and Contact support. GlobalUpgradePrompt decides when to lock.
export const PaywallModal = ({ open, onClose, isLight = false, reason = "default", locked = false }) => {
  const { user, checkAuth, plan, subscription, logout } = useAuth();
  const panelCls = isLight ? "bg-white/95 border-slate-200" : "bg-[#0F211B]/95 border-white/10";
  const headingCls = isLight ? "text-slate-900" : "text-white";
  const bodyCls = isLight ? "text-slate-600" : "text-neutral-300";
  const mutedCls = isLight ? "text-slate-500" : "text-neutral-400";
  const subtleCls = isLight ? "text-slate-400" : "text-neutral-500";
  const cardCls = isLight ? "border-slate-200 bg-slate-50/80" : "border-white/10 bg-[#16332A]/80";
  const inputCls = isLight
    ? "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
    : "bg-black/40 border-white/15 text-white placeholder-neutral-600";
  const [checkoutLoading, setCheckoutLoading] = useState("");
  const [promo, setPromo] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [stripeIntent, setStripeIntent] = useState(null);
  const [promoCodeInput, setPromoCodeInput] = useState("");
  const [promoCodeApplied, setPromoCodeApplied] = useState(null); // { code, percent, label }
  const [promoCodeChecking, setPromoCodeChecking] = useState(false);
  const [quotas, setQuotas] = useState(null);
  const [showCompare, setShowCompare] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!promo) fetch(`${API}/promo`).then((r) => r.json()).then(setPromo).catch(() => {});
    if (!tiers) fetch(`${API}/payments/tiers`).then((r) => r.json()).then((d) => setTiers(d.tiers)).catch(() => {});
    if (!quotas) loadPlanQuotas(API).then(setQuotas);
  }, [open, promo, tiers, quotas]);

  if (!open) return null;

  // A validated promo code takes priority over the sitewide banner promo,
  // matching the backend's own _effective_promo precedence -- same reason:
  // someone who typed in a real code should see and get that specific
  // deal, not whatever's running sitewide.
  const activeDiscountPercent = promoCodeApplied?.percent ?? (promo?.active ? promo.percent : 0);
  const why = PAYWALL_REASONS[reason] || PAYWALL_REASONS.default;

  const promoPrice = (base) => {
    if (activeDiscountPercent > 0) return (base * (1 - activeDiscountPercent / 100)).toFixed(2);
    return base.toFixed(2);
  };

  const applyPromoCode = async () => {
    const code = promoCodeInput.trim();
    if (!code) return;
    setPromoCodeChecking(true);
    try {
      const r = await fetch(`${API}/promo-codes/validate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Invalid promo code.");
      }
      const data = await r.json();
      setPromoCodeApplied(data);
      toast.success(`Code applied — ${data.percent}% off!`);
    } catch (e) {
      setPromoCodeApplied(null);
      toast.error(e.message || "Could not apply that code.");
    } finally {
      setPromoCodeChecking(false);
    }
  };

  const close = () => { onClose && onClose(); setStripeIntent(null); };

  // Someone already on a Stripe plan switches in place (prorated) instead of
  // starting a second subscription -- /subscribe answers "already_active"
  // for them, which used to just close this window with nothing changed.
  const activeSub = ["active", "trialing", "past_due"].includes(subscription?.status) && subscription?.tier ? subscription : null;
  const handleChangeTier = async (tier) => {
    setCheckoutLoading(`${tier}:subscribe`);
    try {
      const r = await fetch(`${API}/payments/stripe/subscription/change-tier`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tier }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Could not change plan");
      await checkAuth();
      window.dispatchEvent(new CustomEvent("subscription:changed"));
      toast.success(`Switched to ${d.tier_label || PLAN_META[tier]?.name}. You only pay the prorated difference for this period.`);
      close();
    } catch (e) {
      toast.error(e.message || "Could not change plan.");
    } finally {
      setCheckoutLoading("");
    }
  };

  const handleSubscribe = async (tier = "professional") => {
    if (!user) { window.location.assign("/signup"); return; } // checkout needs an account
    if (activeSub) { await handleChangeTier(tier); return; }
    setCheckoutLoading(`${tier}:subscribe`);
    try {
      const r = await fetch(`${API}/payments/stripe/subscribe`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin, tier, promo_code: promoCodeApplied?.code }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Could not start subscription");
      }
      const data = await r.json();
      if (data.already_active) {
        await checkAuth();
        if (data.tier && data.tier !== tier) { await handleChangeTier(tier); return; }
        toast.success(`You're already on ${PLAN_META[tier]?.name || "this plan"}.`);
        setCheckoutLoading("");
        close();
        return;
      }
      setStripeIntent({ clientSecret: data.client_secret, subscription: true });
      setCheckoutLoading("");
    } catch (e) {
      toast.error(e.message || "Could not start subscription.");
      setCheckoutLoading("");
    }
  };

  const handleSubscribePaypal = async (tier) => {
    if (!user) { window.location.assign("/signup"); return; }
    setCheckoutLoading(`${tier}:paypal-subscribe`);
    try {
      const r = await fetch(`${API}/payments/paypal/subscribe`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin, tier }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Could not start subscription");
      }
      const data = await r.json();
      if (data.already_active) {
        await checkAuth();
        toast.success("Your subscription is already active.");
        setCheckoutLoading("");
        close();
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.message || "Could not start subscription.");
      setCheckoutLoading("");
    }
  };

  const handleSubscribeSuccess = async () => {
    // Actively sync from Stripe first -- don't just wait on the invoice.paid
    // webhook, which needs `stripe listen` running locally (or a reachable
    // endpoint in prod) and can lag behind the card already being charged.
    try {
      await fetch(`${API}/payments/stripe/subscription/sync`, { method: "POST", credentials: "include" });
    } catch {
      // fall through to polling below regardless
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/payments/stripe/subscription`, { credentials: "include" });
        const data = await r.json();
        if (data.status === "active" || data.status === "trialing") {
          await checkAuth();
          // Tell any mounted AccountSettings to refetch. checkAuth alone
          // isn't enough: the plan card and quota list come from
          // /payments/stripe/subscription and /ai/usage, which that panel
          // only fetches on open -- and on /settings it's mounted with a
          // literal `open`, so it never refetches on its own.
          window.dispatchEvent(new CustomEvent("subscription:changed"));
          toast.success("Subscribed! Downloads unlocked — renews monthly, cancel anytime.");
          close();
          return;
        }
        if (attempts < 5) {
          attempts += 1;
          // Re-sync every attempt too, in case the first sync raced the
          // subscription's invoice not being finalized yet.
          await fetch(`${API}/payments/stripe/subscription/sync`, { method: "POST", credentials: "include" }).catch(() => {});
          setTimeout(poll, 2000);
        } else {
          toast.error("Subscription is still processing. Please check back shortly.");
        }
      } catch (e) {
        toast.error("Could not confirm subscription. If you were charged, access will activate shortly.");
      }
    };
    poll();
  };

  // "Your plan" badge: the live subscription tier, else the effective plan
  // (a one-time pass counts as Starter access but can still be subscribed to).
  const currentPlan = user ? (activeSub?.tier || plan || "free") : null;
  const passUntil = !activeSub && user?.has_download_access ? user?.download_access_until : null;
  const fmtDay = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; } };

  return (
    // Modernized 2026-09-29: frosted, brand-tinted backdrop (never a black
    // slab over the page), rounded glass panel, bottom sheet on phones.
    <div
      className={`fixed inset-0 ${locked ? "z-[95]" : "z-[70]"} flex items-end sm:items-center justify-center sm:p-4 backdrop-blur-md ${isLight ? "bg-[#FBF6EF]/60" : "bg-[#07140F]/55"}`}
      data-testid="paywall-modal"
      onClick={() => { if (!locked && !stripeIntent && !checkoutLoading) close(); }}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-1/3 size-[42rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#A67C52]/20 blur-[120px]" />
        <div className="absolute right-[10%] bottom-[5%] size-[22rem] rounded-full bg-[#22C55E]/10 blur-[100px]" />
      </div>
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        className={`relative w-full overflow-y-auto border shadow-2xl rounded-t-3xl sm:rounded-3xl max-h-[92vh] sm:max-h-[90vh] p-5 sm:p-7 space-y-5 animate-in fade-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200 ${panelCls} ${stripeIntent ? "sm:max-w-lg" : "sm:max-w-6xl"}`}
      >
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#C9A27A]/70 to-transparent" />
        <div className="flex justify-between items-start gap-4">
          <div className="flex items-start gap-3.5">
            <span className="hidden sm:grid size-11 flex-shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-[#C9A27A] to-[#8A6640] text-white shadow-lg shadow-[#A67C52]/30">
              <Crown className="size-5" />
            </span>
            <div>
              <h3 data-testid="paywall-title" className={`text-xl sm:text-2xl font-black tracking-tight ${headingCls}`}>{why.title}</h3>
              <p className={`text-sm mt-1 max-w-2xl ${mutedCls}`}>{why.sub}</p>
              {promo?.active && promo?.percent > 0 && (
                <span data-testid="promo-badge" className="inline-flex items-center gap-1 mt-2 rounded-full bg-gradient-to-r from-[#A67C52] to-[#C9A27A] text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1">
                  <Tag className="size-3" /> {promo.label || `${promo.percent}% OFF`}
                </span>
              )}
            </div>
          </div>
          {!locked && <button data-testid="paywall-close" onClick={close} aria-label="Close" className={`grid size-9 flex-shrink-0 place-items-center rounded-full transition-colors ${isLight ? "bg-slate-100 text-slate-500 hover:bg-slate-200" : "bg-white/5 text-neutral-400 hover:bg-white/10 hover:text-white"}`}>
            <X className="w-4 h-4" />
          </button>}
        </div>

        {stripeIntent ? (
          <StripePaymentForm
            clientSecret={stripeIntent.clientSecret}
            paymentIntentId={stripeIntent.paymentIntentId}
            isSubscription
            onSuccess={handleSubscribeSuccess}
            onCancel={() => setStripeIntent(null)}
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_250px] gap-5 items-start">
          <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 pt-2">
            {PLAN_ORDER.map((key) => {
              const meta = PLAN_META[key];
              const best = key === why.recommend && key !== currentPlan;
              const tier = tiers?.[key];
              const base = tier?.amount ?? meta.price;
              const isFree = key === "free";
              const isCurrent = key === currentPlan;
              // Only a live subscription's own tier is "not buyable"; every
              // other paid tier is a Subscribe (or, for subscribers, a Switch).
              const isOwnedSub = !!activeSub && key === activeSub.tier;
              return (
                <div
                  key={key}
                  data-testid={`paywall-plan-${key}`}
                  className={`relative flex flex-col gap-3 rounded-2xl p-4 transition-all duration-200 ${
                    best
                      ? `border-2 border-[#A67C52] shadow-xl shadow-[#A67C52]/20 ${isLight ? "bg-gradient-to-b from-[#A67C52]/10 to-white" : "bg-gradient-to-b from-[#A67C52]/20 to-[#16332A]"}`
                      : `border hover:-translate-y-0.5 ${isCurrent ? "border-[#22C55E]/50" : "hover:border-[#A67C52]/60"} ${cardCls}`
                  }`}
                >
                  {best && <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-[#A67C52] to-[#C9A27A] text-white text-[9px] font-black uppercase tracking-wider px-2.5 py-1 shadow-md">Recommended</span>}
                  {isCurrent && <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#22C55E] text-white text-[9px] font-black uppercase tracking-wider px-2.5 py-1 shadow-md">{passUntil && !isFree ? `Your access · until ${fmtDay(passUntil)}` : "Your plan"}</span>}
                  <div>
                    <p className={`text-[11px] uppercase tracking-widest font-black ${best ? "text-[#C9A27A]" : mutedCls}`}>{tier?.label || meta.name}</p>
                    <p className={`text-3xl font-black mt-1 tracking-tight ${headingCls}`}>
                      {isFree ? "$0" : (
                        <>
                          {activeDiscountPercent > 0 && <span className={`text-base line-through mr-1 font-normal ${subtleCls}`}>${base}</span>}
                          ${promoPrice(base)}
                        </>
                      )}
                      <span className={`text-sm font-medium ${subtleCls}`}> /mo</span>
                    </p>
                    <p className={`text-xs mt-0.5 ${mutedCls}`}>{meta.tagline}</p>
                  </div>
                  <ul className={`text-xs space-y-2 flex-1 ${bodyCls}`}>
                    {planHighlights(key, quotas).map((f) => (
                      <li key={f} className="flex gap-2"><Check className="w-3.5 h-3.5 mt-0.5 text-[#22C55E] flex-shrink-0" /> <span>{f}</span></li>
                    ))}
                  </ul>
                  {isFree ? (
                    !user ? (
                      <a data-testid="paywall-plan-free-signup" href="/signup" className="w-full rounded-full py-2.5 text-center text-xs font-bold bg-[#22C55E] text-white hover:bg-[#16A34A] shadow-lg shadow-[#22C55E]/20 transition-colors">
                        Create free account
                      </a>
                    ) : activeSub ? (
                      <a data-testid="paywall-plan-free-downgrade" href="/settings" className={`w-full rounded-full py-2.5 text-center text-xs font-bold border transition-colors ${isLight ? "border-slate-200 text-slate-600 hover:border-[#A67C52]" : "border-white/15 text-neutral-300 hover:border-[#A67C52]"}`}>
                        Cancel anytime in Settings
                      </a>
                    ) : locked ? (
                      <div data-testid="paywall-plan-free-used" className={`w-full rounded-full py-2.5 text-center text-xs font-bold border ${isLight ? "border-slate-200 text-slate-500" : "border-white/10 text-neutral-400"}`}>
                        Free tries used this month
                      </div>
                    ) : (
                      <button type="button" data-testid="paywall-plan-free-current" onClick={close} className={`w-full rounded-full py-2.5 text-center text-xs font-bold border transition-colors ${isLight ? "border-slate-200 text-slate-600 hover:border-[#22C55E] hover:text-[#16A34A]" : "border-white/15 text-neutral-200 hover:border-[#22C55E] hover:text-[#4ADE80]"}`}>
                        {currentPlan === "free" ? "Continue with Free" : "Continue for now"}
                      </button>
                    )
                  ) : isOwnedSub ? (
                    <div data-testid={`paywall-plan-${key}-current`} className={`w-full rounded-full py-2.5 text-center text-xs font-bold border ${isLight ? "border-slate-200 text-slate-500" : "border-white/10 text-neutral-400"}`}>
                      Your current plan
                    </div>
                  ) : (
                  <div className="flex flex-col gap-2">
                    <button
                      data-testid={`paywall-subscribe-${key}`}
                      onClick={() => handleSubscribe(key)}
                      disabled={!!checkoutLoading}
                      className={`w-full rounded-full py-2.5 text-xs font-bold flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-60 hover:-translate-y-0.5 ${
                        best
                          ? "text-white bg-gradient-to-r from-[#A67C52] via-[#C9A27A] to-[#A67C52] bg-[length:200%_100%] bg-left hover:bg-right shadow-lg shadow-[#A67C52]/30 ring-1 ring-inset ring-white/20"
                          : isLight ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-white text-[#0F211B] hover:bg-[#E7C9A5]"
                      }`}
                    >
                      {checkoutLoading === `${key}:subscribe` ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-3.5 h-3.5" /> {activeSub ? `Switch to ${meta.name}` : `Get ${meta.name}`}</>}
                    </button>
                    {activeSub ? (
                      <p className={`text-[10px] text-center ${subtleCls}`}>Prorated — you pay only the difference for this period.</p>
                    ) : (
                    <button
                      data-testid={`paywall-subscribe-paypal-${key}`}
                      onClick={() => handleSubscribePaypal(key)}
                      disabled={!!checkoutLoading}
                      className={`w-full rounded-full py-2 text-[11px] font-bold flex items-center justify-center gap-2 border transition-all disabled:opacity-60 ${isLight ? "border-slate-200 text-slate-600 hover:border-[#A67C52] hover:text-[#8A6640]" : "border-white/15 text-neutral-300 hover:border-[#A67C52] hover:text-[#E7C9A5]"}`}
                    >
                      {checkoutLoading === `${key}:paypal-subscribe` ? <Loader2 className="w-4 h-4 animate-spin" /> : "Pay with PayPal"}
                    </button>
                    )}
                  </div>
                  )}
                </div>
              );
            })}
          </div>
          <button type="button" data-testid="paywall-compare-toggle" onClick={() => setShowCompare((v) => !v)} className="inline-flex items-center gap-1 text-xs font-bold text-[#A67C52] hover:underline">
            {showCompare ? "Hide full comparison" : "Compare all features by product →"}
          </button>
          {showCompare && <div className={`rounded-2xl border p-4 ${cardCls}`}><PlanCompare quotas={quotas} isLight={isLight} /></div>}
          <p className={`text-[11px] ${subtleCls}`}>All paid plans renew monthly. Cancel anytime and keep access until the end of the period you paid for.</p>
          </div>

          {/* Trust/promo sidebar -- real content only: Stripe/PayPal are the
              actual processors used above, cancellation matches the real
              "cancel anytime, keep access till period end" behavior, and
              this is the real support mailbox. No fabricated review
              counts/trust badges. */}
          <div className="space-y-3">
            <div className={`rounded-2xl border p-4 space-y-2.5 ${cardCls}`}>
              <p className={`text-[11px] uppercase tracking-widest font-bold flex items-center gap-1.5 ${mutedCls}`}>
                <Tag className="w-3.5 h-3.5 text-[#A67C52]" /> Promo code
              </p>
              <div className="flex gap-1.5">
                <input
                  data-testid="promo-code-input"
                  value={promoCodeInput}
                  onChange={(e) => setPromoCodeInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applyPromoCode()}
                  placeholder="Enter code"
                  disabled={!!promoCodeApplied}
                  className={`flex-1 min-w-0 rounded-full border px-3.5 py-2 text-xs outline-none focus:border-[#A67C52] disabled:opacity-60 ${inputCls}`}
                />
                {promoCodeApplied ? (
                  <button
                    data-testid="promo-code-clear"
                    onClick={() => { setPromoCodeApplied(null); setPromoCodeInput(""); }}
                    className={`rounded-full px-3 text-[11px] font-bold border flex-shrink-0 hover:border-[#A67C52] hover:text-[#A67C52] ${isLight ? "border-slate-200 text-slate-500" : "border-white/15 text-neutral-400"}`}
                  >
                    Clear
                  </button>
                ) : (
                  <button
                    data-testid="promo-code-apply"
                    onClick={applyPromoCode}
                    disabled={promoCodeChecking || !promoCodeInput.trim()}
                    className="rounded-full px-3.5 text-[11px] font-bold bg-[#A67C52] text-white hover:bg-[#8A6640] disabled:opacity-50 flex-shrink-0 flex items-center gap-1"
                  >
                    {promoCodeChecking ? <Loader2 className="w-3 h-3 animate-spin" /> : "Apply"}
                  </button>
                )}
              </div>
              {promoCodeApplied && (
                <p className="text-[11px] text-[#22C55E] flex items-center gap-1.5" data-testid="promo-code-success">
                  <Check className="w-3.5 h-3.5 flex-shrink-0" /> {promoCodeApplied.label || `${promoCodeApplied.percent}% off`} applied
                </p>
              )}
            </div>

            <div className={`rounded-2xl border p-4 space-y-3 text-[11px] ${cardCls} ${mutedCls}`}>
              <div className="flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-[#22C55E] flex-shrink-0 mt-0.5" />
                <span>Secure checkout — payment is handled directly by Stripe or PayPal. We never see or store your card number.</span>
              </div>
              <div className="flex items-start gap-2">
                <RotateCcw className="w-4 h-4 text-[#A67C52] flex-shrink-0 mt-0.5" />
                <span>Cancel anytime from Account Settings — you keep access until the end of the period you already paid for.</span>
              </div>
              <div className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-[#A67C52] flex-shrink-0 mt-0.5" />
                <span>Need help? <a href="mailto:support@resumepilot360.com" className="text-[#A67C52] hover:underline">support@resumepilot360.com</a></span>
              </div>
            </div>
          </div>
          </div>
        )}
        <p className={`text-[11px] text-center flex items-center justify-center gap-1.5 ${subtleCls}`}>
          <Lock className="w-3 h-3" /> Secure checkout via Stripe or PayPal.{user?.email ? ` Signed in as ${user.email}.` : ""}
        </p>
        {locked && (
          <div data-testid="paywall-locked-footer" className={`flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-bold ${subtleCls}`}>
            {user ? (
              <button type="button" data-testid="paywall-locked-signout" onClick={() => { logout && logout(); }} className="hover:text-[#A67C52]">Sign out</button>
            ) : (
              <a href="/login" data-testid="paywall-locked-login" className="hover:text-[#A67C52]">Log in</a>
            )}
            {/* /contact, not mailto: -- a mailto link does nothing on a device with no mail app set up. /contact stays reachable while locked. */}
            <a href="/contact" data-testid="paywall-locked-contact" className="hover:text-[#A67C52]">Contact support</a>
            <a href="/privacy" className="hover:text-[#A67C52]">Privacy</a>
            <a href="/terms" className="hover:text-[#A67C52]">Terms</a>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaywallModal;
